<script lang="ts">
// Two `<script>` blocks on purpose: the regular one exports the
// inject key + interface as named module exports, the `<script setup>`
// below is the SFC's setup function. Vue compiles both — exports here
// are reachable as `import { routeShellDrawersKey } from './RouteShell.vue'`
// while the setup block remains a normal SFC. Splitting is the
// canonical Vue 3 way to expose top-level exports from a `<script
// setup>` SFC.
import type { InjectionKey } from 'vue'

// Provided by RouteShell so descendants can trigger the global
// transient drawers (Add to wiki, Ask) without owning their state.
// Used by HomeView's empty-catalog welcome state today.
export interface RouteShellDrawers {
  openAddToWiki: () => void
  openThreads: () => void
}

export const routeShellDrawersKey: InjectionKey<RouteShellDrawers> = Symbol('routeShellDrawers')
</script>

<script setup lang="ts">
import { provide, ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useSessionStore } from '../stores/session'
import { useThreadsStore } from '../stores/threads'
import { useWikiPagesStore } from '../stores/wiki_pages'
import { useCommandPalette } from '../composables/useCommandPalette'
import { useCommandPaletteItems } from '../composables/useCommandPaletteItems'
import { useAdminSubscriptions } from '../composables/useAdminSubscriptions'
import { useIngestCompletionToast } from '../composables/useIngestCompletionToast'
import { useAgentFeedbackToast } from '../composables/useAgentFeedbackToast'
import AddToWikiDrawer from './AddToWikiDrawer.vue'
import CommandPalette from './CommandPalette.vue'
import DispatchTaskModal from './DispatchTaskModal.vue'
import FailedJobsBanner from './FailedJobsBanner.vue'
import ThreadDrawer from './ThreadDrawer.vue'
import AppNavRail from './AppNavRail.vue'
import AppTabBar from './AppTabBar.vue'

// Single shell every authenticated route mounts inside. Owns the
// global navigation (AppNavRail on desktop, AppTabBar on phones), the two
// transient drawers (Add to wiki,
// Ask), and the command palette — so any route gets the same
// navigation surface for free instead of bolting on its own header.
//
// Why this lives at the route level and not in App.vue: drawers + the
// palette want a Pinia + router context that's only meaningful once
// the user is authenticated. App.vue gates on session and renders the
// sign-in form when unauth'd; the shell sits inside that gate so it
// only mounts for signed-in users.
//
// What stays in the route:
//   - Page selection state (HomeView owns `usePageSelection`)
//   - The sidebar (slot — only HomeView mounts WikiSidebar)
//   - Page-specific UI (NBackTop, breadcrumbs, contextual back-link)
//   - Form state for editor/lint-edit modals
//
// Layout: header on top, body is a flex row carrying an optional
// `#sidebar` slot and a main `<main>` pane. `#back` slot renders
// above the main content for routes that need a contextual return
// link (Editor edit-mode → "Back to <title>"); other routes leave it
// empty and rely on the nav's Today entry for "go home".

withDefaults(
  defineProps<{
    // Reserved for routes that want to render a sidebar (HomeView).
    // Default false so /lint and /p/new don't get an empty sidebar
    // column reserved on the layout grid.
    showSidebar?: boolean
    // Extra class applied to the <main> element. HomeView uses this for
    // its mobile-only `max-sm:hidden` flip when the sidebar takes over
    // the viewport on small screens.
    mainClass?: string | Record<string, boolean>
    // Whether <main> owns the vertical scroll (the default). Full-height
    // routes that manage their own internal scroller — e.g. /agent's chat
    // transcript — pass false so there's a single scroll container instead
    // of a redundant outer one (double-scroll fix).
    mainScroll?: boolean
  }>(),
  { mainScroll: true },
)

defineSlots<{
  sidebar?: () => unknown
  back?: () => unknown
  default: () => unknown
}>()

const router = useRouter()

const session = useSessionStore()
const threadsStore = useThreadsStore()
const wikiPages = useWikiPagesStore()
const { pages: paletteCatalog } = storeToRefs(wikiPages)

useIngestCompletionToast()
useAgentFeedbackToast()
useAdminSubscriptions()

const addToWikiOpen = ref(false)
const threadsOpen = ref(false)
const dispatchTaskOpen = ref(false)
const { open: paletteOpen } = useCommandPalette()

function openAddToWiki() {
  addToWikiOpen.value = true
}

function openThreads() {
  threadsOpen.value = true
}

// Weak-citation nudge: the Ask drawer asks to add a source for a
// thinly-covered answer. Swap drawers — close threads, open Add-to-wiki —
// so the right-placement drawers don't stack.
function openAddSourceFromThread() {
  threadsOpen.value = false
  openAddToWiki()
}

function openDispatchTask() {
  dispatchTaskOpen.value = true
}

// Expose the two transient-drawer triggers to descendants so views
// inside the shell (e.g. HomeView's empty-catalog welcome state) can
// open them inline without re-implementing the state. Inject in
// children with `inject(routeShellDrawersKey)`.
provide(routeShellDrawersKey, { openAddToWiki, openThreads })

function openThreadFromPalette(id: number): void {
  threadsOpen.value = true
  void threadsStore.open(id)
}

// Palette items still need a few callbacks the palette can't derive
// itself (drawer triggers; per-page navigation that resolves an id to
// a path; goToLog has the side effect of marking the log as seen).
// Everything else is plain router.push.
function navigateToPage(id: number) {
  const page = paletteCatalog.value.find((p) => p.id === id)
  if (page) router.push(`/p/${page.path}`)
}

const paletteItems = useCommandPaletteItems({
  openAddToWiki,
  openThreads,
  openDispatchTask,
  openThread: openThreadFromPalette,
  navigateToPage,
  goToAgent: () => router.push('/agent'),
  goToOutputSkills: () => router.push('/output-skills'),
  goToCalendar: () => router.push('/admin/calendar'),
  goToAgentRepos: () => router.push('/admin/agent-repos'),
  goToAgentPrompts: () => router.push('/admin/agent-prompts'),
  goToProductOwnerRuns: () => router.push('/admin/product-owner-runs'),
  newPage: () => router.push({ name: 'wikiPageNew' }),
  goToLog: () => {
    session.markLogSeen()
    router.push('/log')
  },
  goHome: async () => {
    if (router.currentRoute.value.fullPath !== '/') await router.push('/')
  },
  signOut: () => session.signOut(),
})
</script>

<template>
  <div class="flex h-screen overflow-x-hidden">
    <!-- Navigation reads its own state from stores; only drawer and
         palette triggers flow back here because that state lives in
         this shell. The rail shows from md up; below that the tab bar
         carries the same entries (its Menu drawer reuses the rail). -->
    <AppNavRail class="max-md:hidden" @add-to-wiki="openAddToWiki" @ask="openThreads" @search="paletteOpen = true" />

    <div class="flex min-w-0 flex-1 flex-col">
      <FailedJobsBanner />

      <div class="flex flex-1 min-h-0 max-sm:flex-col">
        <slot v-if="showSidebar" name="sidebar" />

        <main
          class="flex-1 min-w-0 px-6 pt-5 pb-6 md:px-10 md:pt-7 md:pb-10 max-sm:px-4 max-sm:pt-3 max-sm:pb-4 relative"
          :class="[mainScroll ? 'overflow-y-auto' : 'overflow-hidden', mainClass]"
        >
          <!-- Contextual back-affordance slot. Routes that have a
               meaningful "where I came from" surface (Editor edit-mode →
               back to source page) render a link here. -->
          <div v-if="$slots.back" class="mb-3" data-testid="route-shell-back">
            <slot name="back" />
          </div>

          <slot />
        </main>
      </div>

      <AppTabBar class="md:hidden" @add-to-wiki="openAddToWiki" @ask="openThreads" @search="paletteOpen = true" />
    </div>

    <AddToWikiDrawer v-model:show="addToWikiOpen" />
    <ThreadDrawer v-model:show="threadsOpen" @add-source="openAddSourceFromThread" />
    <DispatchTaskModal v-model:show="dispatchTaskOpen" />
    <CommandPalette v-model:open="paletteOpen" :items="paletteItems" />
  </div>
</template>
